/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { map, isEmpty, isEqual } from 'lodash'

import cn from 'classnames'

import PropTypes from 'prop-types'

import CartItem from './CartItem'
import styles from './CartItems.module.scss'

import { ReactComponent as CartIcon } from '../assets/icons/EmptyCart.svg'
import { createOrderParams } from '../utils/orderUtils'

import { sendCFDScreenNav, sendUpdateCart } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'

import { getOrderInProgress } from '../selectors/order'
import { isTabbed } from '../utils/orderStates'
import { getTabsModeEnabled } from '../selectors/menus'
import { removeFromCart } from '../actions/cart'
import VNCartTaxButton from '../VNTaxes/components/VNCartTaxButton'
import { getSubtotal, getTax } from '../selectors/cart'
import { centsToDollars } from '../utils/formatters'
import { getIsOrderTaxExempt } from '../VNTaxes/Selectors'
import VNCartServiceCharge from '../VNTaxes/components/VNCartServiceCharge'
import { calculateServiceCharge } from '../utils/totalUtils'
import { getMenuServiceCharge } from '../selectors/items'
import { getCartItems } from '../selectors/cart'
import CartItemsViewModel from '../viewmodels/CartItemsViewModel'
import { getDiscountsAvailable, getSelectedPromotions } from '../selectors/promotion'
import { addPromotionsToOrder, clearPromotionsOnOrder } from '../actions/promotion'
import { setRoute } from '../VNRoutes/ActionCreators'
import { clearOrderInProgress } from '../actions/order'
import VNClearCartButton from '../VNCart/components/VNClearCartButton'
import VNOrderNotes from '../VNCart/components/VNOrderNotes'
import VNLocation from '../VNCart/components/VNLocation'
import VNDiscounts from '../VNCart/components/VNDiscounts'
import { getBarcodeScanningSettings } from '../selectors/config'
import { APPLICATION_STRINGS } from '../strings'

const CartItems = ({ className, remoteOrderTotalLoading }) => {
  const dispatch = useDispatch()
  const classNames = cn(styles.cartItems, className)
  const cfdMode = useSelector((state) => getDeviceMode(state))
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const barcodeScanningSettings = useSelector(getBarcodeScanningSettings)

  const fetchedCartItems = useSelector(getCartItems)
  const cartItems = CartItemsViewModel(fetchedCartItems)
  const discounts = useSelector(getSelectedPromotions)
  const discountsAvailable = useSelector(getDiscountsAvailable)

  const [localCartItems, setLocalCartItems] = useState({})
  const [localDiscounts, setLocalDiscounts] = useState({})
  const tabsModeEnabled = useSelector((state) => getTabsModeEnabled(state))
  const tax = useSelector(getTax)
  const subtotal = useSelector(getSubtotal)
  const taxExempt = useSelector(getIsOrderTaxExempt)
  const serviceCharge = useSelector(getMenuServiceCharge)

  const totalDisplay = useMemo(() => {
    const serviceChargeTotalInCents = calculateServiceCharge(serviceCharge, subtotal) ?? 0

    if (tabsModeEnabled || taxExempt) {
      return centsToDollars(subtotal + serviceChargeTotalInCents)
    }

    return centsToDollars(subtotal + tax + serviceChargeTotalInCents)
  }, [subtotal, tax, tabsModeEnabled, taxExempt, serviceCharge])

  const disclaimer = useMemo(() => {
    if (tabsModeEnabled) {
      return 'Tax not yet calculated.'
    }
    return 'Discounts not yet calculated.'
  }, [tabsModeEnabled])

  // Always call hooks before any early return

  let emptyElement = null

  const updateTheCartWithTheLatest = () => {
    // save it
    setLocalCartItems(cartItems)
    setLocalDiscounts(discounts)

    sendUpdateCart(cartItems, discounts)
  }

  useEffect(() => {
    if (isEmpty(cartItems)) {
      dispatch(clearPromotionsOnOrder())
    }

    if (!isEmpty(orderInProgress)) {
      dispatch(addPromotionsToOrder(createOrderParams(orderInProgress)?.promotions))
    }
  }, [cartItems.length])

  // TODO: (PLOETZ) - Prevent this component from constantly re-rendering (one of offenders is NetworkIndicator.js)
  useEffect(() => {
    if (cfdMode === MODES.POS) {
      // we only care about when the cart items actually update
      if (!isEqual(localCartItems, cartItems) || !isEqual(localDiscounts, discounts)) {
        // if this is ever called, usually on initialization with nothing, we don't need to do anything
        if (isEmpty(cartItems) && isEmpty(localCartItems)) {
          return
        }

        // going from something in the cart to empty
        if (isEmpty(cartItems) && !isEmpty(localCartItems)) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
          updateTheCartWithTheLatest()
          return
        }

        // we have something new in the cart, go to the cart screen and update the data
        if (!isEqual(localCartItems, cartItems)) {
          sendCFDScreenNav(CFD_SCREEN.CART)
          updateTheCartWithTheLatest()
          return
        }

        // the discounts updated, update the cart, but don't navigate
        if (!isEqual(localDiscounts, discounts)) {
          updateTheCartWithTheLatest()
          return
        }
      }
    }
  }, [cartItems, discounts])

  const onRemoveHandler = useCallback(
    (index) => {
      dispatch(removeFromCart({ index }))
    },
    [dispatch],
  )

  const onSelectHandler = useCallback(
    (index) => {
      if (remoteOrderTotalLoading) return
      dispatch(setRoute(`/concession-order/cart/${index}`))
    },
    [dispatch, remoteOrderTotalLoading],
  )

  if (isEmpty(cartItems)) {
    const getEmptyCart = () => {
      if (!isTabbed(orderInProgress)) {
        if (barcodeScanningSettings.barcodeScanningEnabled && barcodeScanningSettings.displayScannableItems) {
          return <p>{APPLICATION_STRINGS.TAP_OR_SCAN_TO_ADD_ITEMS}</p>
        }

        if (barcodeScanningSettings.barcodeScanningEnabled) {
          return <p>{APPLICATION_STRINGS.SCAN_ITEMS_TO_CART}</p>
        }

        return <p>{APPLICATION_STRINGS.EMPTY_CART}</p>
      }
      return (
        <>
          <p>Add to Tab or</p>
          <p
            className={styles.startNewTab}
            onClick={() => {
              dispatch(clearOrderInProgress())
            }}
          >
            Start New Order
          </p>
        </>
      )
    }

    return (
      <div className={classNames}>
        <div className={styles.empty}>
          <CartIcon />
          {getEmptyCart()}
        </div>
      </div>
    )
  }

  return (
    <div className={classNames}>
      {emptyElement}

      {/*Real items in the cart*/}
      {map(cartItems, (cartItem, index, items) => {
        const { variant, ...rest } = cartItem
        return (
          <CartItem
            key={`${cartItem.id}-${index}`}
            {...rest}
            index={index}
            modifiers={cartItem.modifiersString}
            onRemove={onRemoveHandler}
            onSelect={onSelectHandler}
            className={styles.cartItem}
            remoteOrderTotalLoading={remoteOrderTotalLoading}
            realCartItem
          />
        )
      })}

      <VNCartTaxButton />
      <VNCartServiceCharge />

      {/*Total Line*/}
      <div className={styles.totalContainer}>
        <span className={styles.total}>Total</span>
        <span className={styles.total}>{totalDisplay}</span>
      </div>
      {/*Tax / discount not yet calculated line*/}
      <div className={styles.discountsDisclaimerContainer}>
        {discountsAvailable && <span className={styles.discountsDisclaimer}>{disclaimer}</span>}
      </div>

      <div className={styles.separator} />
      <VNLocation />
      <VNOrderNotes />
      <VNDiscounts />
      <VNClearCartButton />
    </div>
  )
}

CartItems.propTypes = {
  className: PropTypes.string,
}

export default CartItems
